/**
 * The shared 1–5 impact scale: one colour and one word per step, used by
 * the ImpactPill, the two client-rendered radars (as concentric bands) and
 * the radar scale key. The two variants share colours and differ only at
 * step 5: NatSec says "Critical", the harm-severity scale "Catastrophic".
 */
export type ImpactPillVariant = 'natsec' | 'standard';

export const IMPACT_PALETTE: Array<{ score: number; bg: string }> = [
  { score: 1, bg: '#94A3B8' },
  { score: 2, bg: '#60A5FA' },
  { score: 3, bg: '#F59E0B' },
  { score: 4, bg: '#EF4444' },
  { score: 5, bg: '#911A33' },
];

export const IMPACT_LABELS: Record<ImpactPillVariant, Record<number, string>> = {
  natsec:   { 1: 'Negligible', 2: 'Minor', 3: 'Substantial', 4: 'Severe', 5: 'Critical' },
  standard: { 1: 'Negligible', 2: 'Minor', 3: 'Substantial', 4: 'Severe', 5: 'Catastrophic' },
};

/** Where each scale's levels are defined, for "based on this scale" links. */
export const NATSEC_FRAMEWORK_URL = 'https://airisk.mit.edu/ai-incident-tracker/natsec-impact-framework';

/** `#rrggbb` → `rgba(r,g,b,alpha)`. */
export function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Concentric-band tints for a radar with `max: 5` and `splitNumber: 5`,
 * ordered innermost → outermost (step 1 → step 5). Tinted strongly enough to read as
 * the same colours as the scale key's swatches — a lighter wash made the
 * two look like different scales — but still a ground: the data polygon is
 * drawn in neutral ink so it stays the figure. The key beneath carries the
 * same colours at full strength beside their words, so colour is never the
 * only encoding.
 */
export const RADAR_BAND_ALPHAS = [0.45, 0.52, 0.58, 0.66, 0.80];
export function radarBandColors(): string[] {
  return IMPACT_PALETTE.map((p, i) => withAlpha(p.bg, RADAR_BAND_ALPHAS[i]));
}
